import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, UrlTree } from '@angular/router';
import { FeatureFlagsService } from '../services/featureFlags.service';
import { NotificationService } from '../services/notification.service';
import { UserService } from '../services/user/user.service';
import { TerritoryService } from '../services/admin/territory.service';

/** Territories opens only for these roles; Administrator alone does not. */
export const TERRITORY_ROLES = ['Territory Manager', 'Territory Viewer'];

export function hasTerritoryRole(roles: string[] | undefined): boolean {
  return !!roles?.some(role => TERRITORY_ROLES.includes(role));
}

/** A rep is a buyer-side user: neither seller nor administrator. Same rule as the API. */
function isRep(user: any): boolean {
  return !user?.is_seller && !user?.is_admin;
}

/** The rep page reads the effective coverage table, which is a rep's real book only once the
 *  namespace has cut over to it (process.territory_effective_cutover). Same rule as the API. */
export function repTerritoryEnabled(flags: FeatureFlagsService): boolean {
  return (
    !!flags.getByEnv('process', 'territories') &&
    !!flags.getByEnv('process', 'territory_effective_cutover')
  );
}

/** process.territories plus a Territory role, on the right side of the portal. */
async function territoryGuard(forReps: boolean): Promise<boolean | UrlTree> {
  const router = inject(Router);
  const flags = inject(FeatureFlagsService);
  const enabled = forReps
    ? repTerritoryEnabled(flags)
    : !!flags.getByEnv('process', 'territories');
  if (!enabled) {
    return router.parseUrl('/');
  }
  const user = (await inject(UserService).getMe().toPromise())?.data;
  return (
    (hasTerritoryRole(user?.roles) && isRep(user) === forReps) ||
    router.parseUrl('/')
  );
}

/** /territories: sellers and administrators with a Territory role. */
export const territoryAccessGuard = () => territoryGuard(false);

/** /territories/my: reps with a Territory role whose company is in a manufacturer's rep pool.
 *  The pool is only known to the API, so the guard asks it before the page opens. */
export async function myTerritoryGuard(): Promise<boolean | UrlTree> {
  const router = inject(Router);
  const notify = inject(NotificationService);
  const api = inject(TerritoryService);
  const allowed = await territoryGuard(true);
  if (allowed !== true) return allowed;
  api.repView = true;
  try {
    await api.context(true).toPromise();
    return true;
  } catch (err) {
    notify.showErrorMessage(contextErrorMessage(err));
    return router.parseUrl('/');
  }
}

/** The API's rep gate answers 404 while the feature is off for the user (a flag flip can take
 *  minutes to reach the server), 403 when their company is in no manufacturer's rep pool. */
function contextErrorMessage(err: unknown): string {
  const status = err instanceof HttpErrorResponse ? err.status : null;
  if (status === 404)
    return "My Territory isn't switched on for your account yet.";
  if (status === 403)
    return "My Territory isn't available yet: your company isn't linked to a manufacturer. Ask your administrator.";
  return "My Territory couldn't load. Try again in a minute.";
}
